import { Popconfirm } from 'antd';
import React, { forwardRef, useImperativeHandle, useRef, useEffect, useState } from 'react';
import {
  setTableColumnSelection,
  clearAzureReservationOrdersMessages,
  azureReservationOrdersSelector,
} from '../../../../store/azure/azureReservationOrders/azureReservationOrders.reducer';
import { useAppDispatch, useAppSelector } from '../../../../store/app.hooks';
import {
  deleteAzureReservationOrders,
  searchAzureReservationOrders,
} from '../../../../store/azure/azureReservationOrders/azureReservationOrders.action';
import _ from 'lodash';
import azureReservationOrdersService from '../../../../services/azure/azureReservationOrders/azureReservationOrders.service';
import {
  FilterByDateSwap,
  FilterWithSwapOption,
} from '../../../../common/components/DataTable/DataTableFilters';
import { IMainTable, ISearch } from '../../../../common/models/common';
import { useHistory } from 'react-router-dom';
import DataTable from '../../../../common/components/DataTable';
import ability, { Can } from '../../../../common/ability';
import { Action, Page } from '../../../../common/constants/pageAction';
import { showDateFromApi } from '../../../../common/helperFunction';

const MainTable: React.ForwardRefRenderFunction<unknown, IMainTable> = (props, ref) => {
  const {
    setSelectedId,
    setShowSelectedListModal,
    setValuesForSelection,
    isMultiple,
    tableButtons,
  } = props;
  const azureReservationOrders = useAppSelector(azureReservationOrdersSelector);
  const dispatch = useAppDispatch();
  const dataTableRef = useRef(null);
  const history = useHistory();
  const [ObjectForColumnFilter, setObjectForColumnFilter] = useState({});

  useImperativeHandle(ref, () => ({
    refreshData() {
      dataTableRef?.current.refreshData();
    },
  }));

  useEffect(() => {
    if (isMultiple) {
      dataTableRef?.current.getValuesForSelection();
    }
  }, [isMultiple]);

  const exportExcelFile = (searchData: ISearch) => {
    return azureReservationOrdersService.exportExcelFile(searchData);
  };

  const FilterBySwap = (dataIndex: string, form, isRelativeDropDownFlag?: boolean) => {
    return FilterWithSwapOption(
      dataIndex,
      azureReservationOrders.search.tableName,
      form,
      null,
      ObjectForColumnFilter,
      null,
      null,
      isRelativeDropDownFlag
    );
  };

  const FilterByDateSwapTable = (dataIndex: string, tableName: string, form: any) => {
    return FilterByDateSwap(dataIndex, tableName, form, null, ObjectForColumnFilter);
  };

  const getTableColumns = (form) => {
    return [
      {
        title: <span className="dragHandler">ID</span>,
        column: 'id',
        sorter: true,
        ellipsis: true,
        children: [
          {
            title: FilterBySwap('id', form),
            dataIndex: 'id',
            key: 'id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Tenant Name</span>,
        column: 'TenantId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('tenant_id', form, true),
            dataIndex: 'tenant_name',
            key: 'tenant_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Company Name</span>,
        column: 'CompanyId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('company_id', form, true),
            dataIndex: 'company_name',
            key: 'company_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Bu Name</span>,
        column: 'BU_Id',
        sorter: true,
        children: [
          {
            title: FilterBySwap('bu_id', form, true),
            dataIndex: 'bu_name',
            key: 'bu_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Date Added</span>,
        column: 'Date Added',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable('date_added', azureReservationOrders.search.tableName, form),
            dataIndex: 'date_added',
            key: 'date_added',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Reservation Order Id</span>,
        column: 'ReservationOrderId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('reservation_order_id', form),
            dataIndex: 'reservation_order_id',
            key: 'reservation_order_id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Display Name</span>,
        column: 'DisplayName',
        sorter: true,
        children: [
          {
            title: FilterBySwap('display_name', form),
            dataIndex: 'display_name',
            key: 'display_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Request Date Time</span>,
        column: 'RequestDateTime',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable(
              'request_date_time',
              azureReservationOrders.search.tableName,
              form
            ),
            dataIndex: 'request_date_time',
            key: 'request_date_time',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Created Date Time</span>,
        column: 'CreatedDateTime',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable(
              'created_date_time',
              azureReservationOrders.search.tableName,
              form
            ),
            dataIndex: 'created_date_time',
            key: 'created_date_time',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Expiry Date</span>,
        column: 'ExpiryDate',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable('expiry_date', azureReservationOrders.search.tableName, form),
            dataIndex: 'expiry_date',
            key: 'expiry_date',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Benefit Start Date</span>,
        column: 'BenefitStartDate',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable(
              'benefit_start_date',
              azureReservationOrders.search.tableName,
              form
            ),
            dataIndex: 'benefit_start_date',
            key: 'benefit_start_date',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Original Quantity</span>,
        column: 'OriginalQuantity',
        sorter: true,
        children: [
          {
            title: FilterBySwap('original_quantity', form),
            dataIndex: 'original_quantity',
            key: 'original_quantity',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Term</span>,
        column: 'Term',
        sorter: true,
        children: [
          {
            title: FilterBySwap('term', form),
            dataIndex: 'term',
            key: 'term',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Provisioning State</span>,
        column: 'ProvisioningState',
        sorter: true,
        children: [
          {
            title: FilterBySwap('provisioning_state', form),
            dataIndex: 'provisioning_state',
            key: 'provisioning_state',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Billing Plan</span>,
        column: 'BillingPlan',
        sorter: true,
        children: [
          {
            title: FilterBySwap('billing_plan', form),
            dataIndex: 'billing_plan',
            key: 'billing_plan',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Original Request Id</span>,
        column: 'OriginalRequestId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('original_request_id', form),
            dataIndex: 'original_request_id',
            key: 'original_request_id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Billing Account Id</span>,
        column: 'BillingAccountId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('billing_account_id', form),
            dataIndex: 'billing_account_id',
            key: 'billing_account_id',
            ellipsis: true,
          },
        ],
      },
    ];
  };

  const removeAzureReservationOrders = (id: number) => {
    dispatch(deleteAzureReservationOrders(id));
  };

  const tableAction = (_unused, data: any) => (
    <div className="btns-block">
      <Can I={Action.Update} a={Page.AzureReservationOrders}>
        <a
          className="action-btn"
          onClick={() => {
            setSelectedId(data.id);
            history.push(`/azure/azure-reservation-orders/${data.id}`);
          }}
        >
          <img src={`${process.env.PUBLIC_URL}/assets/images/ic-edit.svg`} alt="" />
        </a>
      </Can>
      <Can I={Action.Delete} a={Page.AzureReservationOrders}>
        <Popconfirm title="Delete Record?" onConfirm={() => removeAzureReservationOrders(data.id)}>
          <a href="#" title="" className="action-btn">
            <img src={`${process.env.PUBLIC_URL}/assets/images/ic-delete.svg`} alt="" />
          </a>
        </Popconfirm>
      </Can>
    </div>
  );

  return (
    <>
      <DataTable
        ref={dataTableRef}
        showAddButton={ability.can(Action.Add, Page.AzureReservationOrders)}
        setSelectedId={setSelectedId}
        tableAction={tableAction}
        exportExcelFile={exportExcelFile}
        getTableColumns={getTableColumns}
        reduxSelector={azureReservationOrdersSelector}
        searchTableData={searchAzureReservationOrders}
        clearTableDataMessages={clearAzureReservationOrdersMessages}
        setTableColumnSelection={setTableColumnSelection}
        setShowSelectedListModal={setShowSelectedListModal}
        setValuesForSelection={setValuesForSelection}
        setObjectForColumnFilter={setObjectForColumnFilter}
        tableButtons={tableButtons}
      />
    </>
  );
};

export default forwardRef(MainTable);
